import { ref, inject } from 'vue'
import { defineStore } from 'pinia'
import { RequestService } from '../services/RequestService'
import { groupBy, isEmpty } from 'lodash'
import axios from 'axios'

const { simpleGet, handleErrors, post } = RequestService()

export const useRenewalEmployerStore = defineStore(
  'renewalEmployer',
  () => {
    const isLoading = ref(false)
    const planQuestions = ref([])
    const assessKeys = ref([])
    const mainAssessKey = ref('')
    const originalQuestions = ref([])
    const erisaDate = ref('')
    const isRenewalDisabled = ref(false)
    const functionsBaseUrl = inject('functionsBaseUrl')

    /**
     * Load questions and answers for a specific plan year
     * @param {number} planYearId - The ID of the plan year to load questions for
     * @returns {Promise<void>}
     */
    async function loadQuestionsAndAnswers(planYearId) {
      if (!planYearId) {
        handleErrors('loadQuestionsAndAnswers', new Error('Missing required parameters for loadQuestionsAndAnswers'))
        return
      }

      isLoading.value = true

      try {
        let endpoints = [
          `${functionsBaseUrl}/questionsGateway/api/v1/plan-years/${planYearId}/questions`,
        ]

        await axios.all(endpoints.map((url) => simpleGet('loadQuestionsAndAnswers', url))).then(
          axios.spread((inQuestions) => {
            originalQuestions.value = []
            const planQuestionsInResponse = inQuestions.data.data.filter((val) => val.section_id === '10')
            for (let i = 0; i < planQuestionsInResponse.length; i++) {
              let element = planQuestionsInResponse[i]
              element.options = element.options.values
              switch (element.type) {
                case 0:
                  element.answer = element.answer ? ref(JSON.parse(element.answer)) : ref('')
                  break
                case 1:
                  element.answer = element.answer ? ref(JSON.parse(element.answer)) : ref([])
                  break
                case 2:
                case 3:
                  element.answer = element.answer ? ref(JSON.parse(element.answer)) : ref()
                  break
                case 4:
                  if (
                    element.answer !== '1' &&
                    element.answer !== '2' &&
                    !isEmpty(element.answer)
                  ) {
                    erisaDate.value = element.answer
                    element.answer = 0
                  } else if (isEmpty(element.answer)) {
                    erisaDate.value = ''
                    element.answer = ''
                  }
                  break
              }
              //we review the action_items_resp_party field
              element.action_items_resp_party =
                element.action_items_resp_party && element.action_items_resp_party.length > 0 ?
                  JSON.parse(element.action_items_resp_party)
                : ref([false, false, false])

              originalQuestions.value.push(element)
            }

            planQuestions.value = groupBy(
              originalQuestions.value.filter((val) => val.section_id === '10'),
              'title'
            )
            mainAssessKey.value = Object.keys(planQuestions.value)[0]
          })
        )
      } catch (error) {
        handleErrors('Error loading questions:', error)
      } finally {
        isLoading.value = false
      }
    }

    /**
     * Check if questions are complete
     * @param {Array} questions - Array of questions to check
     * @returns {boolean} - True if all questions are complete
     */
    function isQuestionsComplete(questions) {
      if (!questions) {
        return false
      }
      let pendingQuestions = questions.filter((val) => {
        if (val.answer !== undefined) {
          if (typeof val.answer === 'object') {
            return Object.keys(val.answer).length === 0
          }
          return val.answer === '' || val.answer === null || val.answer === undefined
        } else {
          return true
        }
      })
      return pendingQuestions.length > 0 ? false : true
    }

    /**
     * Check if plan questionnaire is complete
     * @returns {boolean} - True if plan questionnaire is complete
     */
    function isPlanComplete() {
      if (!planQuestions.value || Object.keys(planQuestions.value).length === 0) {
        return false
      }
      const section = Object.values(planQuestions.value)[0] ?? []
      if (section.length === 0) {
        return false
      }
      const requiredPlanQuestions = section.filter((question) => !question.isOptional)
      return isQuestionsComplete(requiredPlanQuestions)
    }

    async function savePlanQuestionsOnRenewal(planYearId, questionsToProcess) {
      try {
        const innerQuestions = questionsToProcess.map((element) => {
          if (element.type === 4) {
            return {
              question_id: element.id,
              employer_plan_years_id: planYearId,
              answer: element.answer,
              notes: element.notes,
              note_updated_at: element.note_updated_at || '',
              note_updated_by: element.note_updated_by || '',
            }
          }
          return {
            question_id: element.id,
            employer_plan_years_id: planYearId,
            answer: JSON.stringify(element.answer),
            notes: element.notes || '',
            note_updated_at: element.note_updated_at || '',
            note_updated_by: element.note_updated_by || '',
            action_items_notes: element.action_items_notes,
            action_items_resp_party: JSON.stringify(element.action_items_resp_party),
            action_items_note_updated_at: element.action_items_note_updated_at || '',
            action_items_note_updated_by: element.action_items_note_updated_by || ''
          }
        })

        const url = `${functionsBaseUrl}/questionsGateway/api/v1/answers`
        await post('saveAnswersRenewalPlan', url, innerQuestions)
      } catch (error) {
        handleErrors('Error saving plan questions on renewal:', error)
      }
    }

    /**
     * Reset the renewal employer store
     */
    function resetRenewalStore() {
      planQuestions.value = []
      assessKeys.value = []
      mainAssessKey.value = ''
      originalQuestions.value = []
    }
    
    /**
     * Update isRenewalDisabled state
     * @param {boolean} value - New value for isRenewalDisabled
     */
    function updateIsRenewalDisabled(value) {
      isRenewalDisabled.value = value
    }

    return {
      isLoading,
      planQuestions,
      assessKeys,
      mainAssessKey,
      originalQuestions,
      erisaDate,
      isRenewalDisabled,
      savePlanQuestionsOnRenewal,
      loadQuestionsAndAnswers,
      isPlanComplete,
      resetRenewalStore,
      updateIsRenewalDisabled
    }
  }
)
